import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API } from '../utils/helpers';

// The shop intake form (17 Oct). A shop owner arrives here from the private
// link in their invite email: /onboard/<token>. No login. They fill in the
// sections at their own pace, everything saving as they go, accept the beta
// terms, submit, and then start their subscription.
//
// The sections and fields come from the server (helpers/intake.js SECTIONS),
// which also validates them, so this page only draws what it is given.

const C = {
  bg: '#f0f0eb', card: '#ffffff', border: '#e0e0d8', inputBorder: '#d0d0c8', input: '#f8f8f4',
  text: '#1a1a1a', sub: '#55554e', navy: '#1a2336', accent: '#F4622A',
  ok: '#2d6a3e', warnBg: '#fff4ec', warnBorder: '#f3c9ad', error: '#b3261e',
  chosen: '#e8edf6',
};
const font = "'IBM Plex Sans', -apple-system, system-ui, sans-serif";
const inputStyle = (bad) => ({
  minHeight: 44, padding: '0 12px', border: `1px solid ${bad ? C.error : C.inputBorder}`, borderRadius: 8,
  background: C.input, font: 'inherit', fontSize: 15, fontWeight: 400, color: C.text, width: '100%', boxSizing: 'border-box',
});
const btn = (primary) => ({
  minHeight: 44, padding: '0 20px', borderRadius: 8, font: 'inherit', fontWeight: 700, cursor: 'pointer',
  border: primary ? 'none' : `1px solid ${C.inputBorder}`, background: primary ? C.navy : '#fff', color: primary ? '#fff' : C.text,
});

// Same rule as the server's suggestion (helpers/intake.js slugify).
const slugify = (name) => String(name || '').toLowerCase().replace(/&/g, ' and ').replace(/['\u2019]/g, '')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 31).replace(/-+$/, '');
const tokenFromPath = () => (window.location.pathname.split('/')[2] || '').trim();
const optValue = (o) => (Array.isArray(o) ? o[0] : o);
const optLabel = (o) => (Array.isArray(o) ? o[1] : o);
const shown = (f, a) => {
  const cond = f.showIf || f.requiredIf;
  return !cond || a[cond[0]] === cond[1];
};
const required = (f, a) => !!f.required || (!!f.requiredIf && a[f.requiredIf[0]] === f.requiredIf[1]);

function Field({ f, value, problem, onChange, onBlur, sectionOptional }) {
  const id = `f-${f.key.replace(/\W/g, '-')}`;
  const label = (
    <span style={{ fontWeight: 600, fontSize: 14 }}>
      {f.label}{!f.required && !f.requiredIf && !sectionOptional ? <span style={{ fontWeight: 400, color: C.sub }}> (optional)</span> : null}
    </span>
  );
  const note = problem
    ? <span role="alert" style={{ fontSize: 13, color: C.error }}>{problem}</span>
    : f.hint ? <span style={{ fontSize: 13, color: C.sub, lineHeight: 1.45 }}>{f.hint}</span> : null;

  // "What your shop misses without texting": shown when they choose to set
  // texting up later. Information, not a question.
  if (f.type === 'notice') {
    return (
      <div role="note" style={{ gridColumn: '1 / -1', background: C.warnBg, border: `1px solid ${C.warnBorder}`, borderRadius: 12, padding: '18px 20px',
        display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>{f.label}</div>
        <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6, lineHeight: 1.45, fontSize: 14.5 }}>
          {(f.items || []).map(([what, why]) => <li key={what}><strong>{what}.</strong> {why}</li>)}
        </ul>
        {(f.footer || []).map((para, n) => (
          <p key={n} style={{ margin: 0, fontSize: 14.5, lineHeight: 1.5, fontWeight: n === 0 ? 600 : 400 }}>{para}</p>
        ))}
      </div>
    );
  }
  if (f.type === 'radio') {
    return (
      <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10, gridColumn: '1 / -1' }}>
        <legend style={{ marginBottom: 8 }}>{label}</legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          {f.options.map(o => {
            const on = value === o[0];
            return (
              <label key={o[0]} style={{ display: 'flex', gap: 12, padding: 16, borderRadius: 12, cursor: 'pointer',
                background: on ? C.chosen : '#fff', border: `2px solid ${on ? C.navy : C.border}`,
                boxShadow: on ? '0 0 0 3px rgba(26,35,54,0.12)' : 'none' }}>
                <input type="radio" name={id} checked={on} onChange={() => { onChange(o[0]); onBlur(o[0]); }}
                  style={{ width: 20, height: 20, marginTop: 2, accentColor: C.navy, flex: '0 0 auto' }} />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 700 }}>{o[1]}</span>
                  <span style={{ fontSize: 14, color: C.sub, lineHeight: 1.45 }}>{o[2]}</span>
                </span>
              </label>
            );
          })}
        </div>
        {note}
      </fieldset>
    );
  }
  return (
    <label htmlFor={id} style={{ display: 'flex', flexDirection: 'column', gap: 6, gridColumn: f.type === 'textarea' ? '1 / -1' : undefined }}>
      {label}
      {f.type === 'select' ? (
        <select id={id} value={value || ''} onChange={e => { onChange(e.target.value); onBlur(e.target.value); }} style={{ ...inputStyle(!!problem), padding: '0 10px' }}>
          <option value="">Choose…</option>
          {f.options.map(o => <option key={optValue(o)} value={optValue(o)}>{optLabel(o)}</option>)}
        </select>
      ) : f.type === 'textarea' ? (
        <textarea id={id} rows={3} value={value || ''} onChange={e => onChange(e.target.value)} onBlur={e => onBlur(e.target.value)}
          placeholder={f.placeholder || ''} style={{ ...inputStyle(!!problem), padding: '10px 12px', resize: 'vertical' }} />
      ) : (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input id={id} type={f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : f.type === 'url' ? 'url' : 'text'}
            value={value || ''} maxLength={f.max || 300} placeholder={f.placeholder || ''}
            onChange={e => onChange(e.target.value)} onBlur={e => onBlur(e.target.value)} style={inputStyle(!!problem)} />
        </span>
      )}
      {f.prefix && (
        <span style={{ fontSize: 13, color: C.sub, overflowWrap: 'anywhere' }}>Your page: {f.prefix}<wbr /><strong style={{ color: C.text }}>{value || '…'}</strong></span>
      )}
      {note}
    </label>
  );
}

export default function IntakeForm() {
  const token = tokenFromPath();
  const [view, setView] = useState(null);
  const [answers, setAnswers] = useState({});
  const [problems, setProblems] = useState({});
  const [progress, setProgress] = useState([]);
  const [at, setAt] = useState(0);
  const [loadError, setLoadError] = useState('');
  const [saveState, setSaveState] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsName, setTermsName] = useState('');
  const [busy, setBusy] = useState(false);
  const dirty = useRef({});

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/intake/${encodeURIComponent(token)}`);
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.error || 'This link could not be opened.');
      setView(out);
      setAnswers(out.answers || {});
      setProblems(out.problems || {});
      setProgress(out.progress || []);
    } catch (err) { setLoadError(err.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // `visited` names a section the owner finished with "Save and continue", so
  // one with nothing required (the website pages) is ticked only then.
  // Saves go one at a time, in order: leaving a box and clicking a choice fire
  // two saves together, and the server already merges them safely, but in
  // order the screen also shows the result of the latest one.
  const queue = useRef(Promise.resolve(true));
  const save = (visited) => {
    queue.current = queue.current.then(() => saveNow(visited), () => saveNow(visited));
    return queue.current;
  };
  const saveNow = async (visited) => {
    const changes = dirty.current;
    if (!Object.keys(changes).length && !visited) return true;
    dirty.current = {};
    setSaveState('Saving…');
    try {
      const r = await fetch(`${API}/api/intake/${encodeURIComponent(token)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ answers: changes, visited }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.error || 'Could not save.');
      setProblems(out.problems || {});
      setProgress(out.progress || []);
      setSaveState('Saved');
      return true;
    } catch (err) {
      dirty.current = { ...changes, ...dirty.current };
      setSaveState(`Not saved: ${err.message}`);
      return false;
    }
  };

  const setValue = (key, v) => {
    setAnswers(a => {
      const next = { ...a, [key]: v };
      // Keep the suggested booking address in step with the shop name, until
      // the owner types an address of their own.
      if (key === 'name' && (!a.slug || a.slug === slugify(a.name))) {
        next.slug = slugify(v);
        dirty.current.slug = next.slug;
      }
      return next;
    });
    dirty.current[key] = v;
    setSaveState('');
  };

  if (loadError) {
    return (
      <Page>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, maxWidth: 560 }}>
          <h1 style={{ margin: '0 0 8px', fontSize: 24 }}>This link cannot be opened</h1>
          <p style={{ margin: 0, color: C.sub, lineHeight: 1.5 }}>{loadError}</p>
        </div>
      </Page>
    );
  }
  if (!view) return <Page><div style={{ color: C.sub }}>Loading…</div></Page>;

  // The carrier-registration sections drop out for a shop setting texting up later.
  const sections = (view.sections || []).filter(s => !(s.skipIf && answers[s.skipIf[0]] === s.skipIf[1]));
  const locked = view.status === 'submitted' || view.status === 'created';
  const onReview = at >= sections.length;
  const section = sections[at];
  // Only the sections on screen: a skipped one is "complete" but not one of theirs.
  const doneCount = progress.filter(p => p.complete && sections.some(s => s.key === p.key)).length;

  // Only "Save and continue" marks a section as gone through: clicking past it
  // in the side list is not a yes (17 Oct: the website step was ticked by
  // being opened and left).
  const go = async (i, { confirm = false } = {}) => { await save(confirm && section ? section.key : undefined); setAt(i); window.scrollTo(0, 0); };

  const submit = async () => {
    setBusy(true); setSubmitError('');
    await save();
    try {
      const r = await fetch(`${API}/api/intake/${encodeURIComponent(token)}/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ termsAccepted, termsName, termsVersion: view.terms.version }),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (out.problems) setProblems(out.problems);
        throw new Error(out.error || 'Could not submit.');
      }
      setView(out);
      window.scrollTo(0, 0);
    } catch (err) { setSubmitError(err.message); }
    setBusy(false);
  };

  if (locked) {
    // The last screen says what happens next and that they are done here, so
    // it does not read as a page that stopped half way (17 Oct).
    const created = view.status === 'created';
    const later = answers['numberPlan.plan'] === 'later';
    const email = answers.ownerEmail || '';
    const next = created ? [] : [
      ['We check your details', 'Usually within a day. If anything needs changing, we email you this link with a note.'],
      ['You choose your password', `We email ${email || 'you'} a link to set your password and sign in.`],
      later
        ? ['Texting waits until you want it', 'Ask us whenever you are ready and we start your registration.']
        : ['We register your texting', 'We file it with the phone carriers. Approval takes a few days to two weeks, and we tell you when texting is live.'],
    ];
    return (
      <Page>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, maxWidth: 640, width: '100%', boxSizing: 'border-box',
          alignSelf: 'center', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span aria-hidden="true" style={{ width: 36, height: 36, flex: '0 0 36px', borderRadius: '50%', background: C.ok, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700 }}>✓</span>
            <h1 style={{ margin: 0, fontSize: 26 }}>
              {created ? `${view.shopName} is set up on Spinboard` : `Thanks, ${view.shopName} is submitted`}
            </h1>
          </div>
          {created ? (
            <p style={{ margin: 0, color: C.sub, lineHeight: 1.55 }}>Check your email for the link to choose your password and sign in.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontWeight: 700 }}>What happens next</div>
              <ol style={{ margin: 0, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 8, lineHeight: 1.5 }}>
                {next.map(([what, why]) => <li key={what}><strong>{what}.</strong> <span style={{ color: C.sub }}>{why}</span></li>)}
              </ol>
            </div>
          )}
          {view.signedUp ? (
            <div style={{ background: '#eaf5ee', border: '1px solid #b9dcc5', borderRadius: 10, padding: '14px 16px', lineHeight: 1.5 }}>
              <strong>Your subscription is started.</strong> Nothing else to do for now.
            </div>
          ) : view.signupUrl ? (
            <div style={{ background: C.warnBg, border: `1px solid ${C.warnBorder}`, borderRadius: 10, padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 17 }}>One more thing: start your subscription</div>
              <div style={{ lineHeight: 1.5 }}>{view.terms.signupNote}</div>
              <div><a href={view.signupUrl} style={{ ...btn(true), display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>Start my subscription</a></div>
            </div>
          ) : null}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ margin: 0, lineHeight: 1.55 }}>
              {view.signedUp || !view.signupUrl
                ? 'That is everything for now. You can close this page.'
                : 'Once your subscription is started, that is everything for now. You can close this page.'}
              {' '}Everything is saved{email ? `, and we will email ${email}` : ''}.
            </p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <a href="https://spinboard.app" style={{ color: C.navy, fontWeight: 600 }}>Visit spinboard.app</a>
              {created && <a href={`${window.location.origin}/`} style={{ color: C.navy, fontWeight: 600 }}>Sign in to Spinboard</a>}
            </div>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h1 style={{ margin: 0, fontSize: 30, fontWeight: 700 }}>Set up {view.shopName} on Spinboard</h1>
        <div style={{ color: C.sub, maxWidth: 680, lineHeight: 1.5 }}>
          About 15 minutes. Have your EIN letter handy. Everything saves as you go, so you can stop and come back with this same link.
        </div>
      </div>

      {view.note && (
        <div style={{ background: C.warnBg, border: `1px solid ${C.warnBorder}`, borderRadius: 10, padding: '14px 16px', lineHeight: 1.5 }}>
          <strong>Please change a few things and submit again:</strong>
          <div style={{ marginTop: 6, whiteSpace: 'pre-line' }}>{view.note}</div>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
        <nav aria-label="Sections" style={{ flex: '1 1 230px', maxWidth: 290, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {sections.map((s, i) => {
            const p = progress.find(x => x.key === s.key) || {};
            const cur = i === at;
            return (
              <button key={s.key} onClick={() => go(i)} aria-current={cur ? 'step' : undefined}
                style={{ minHeight: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '0 12px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${cur ? C.navy : 'transparent'}`, background: cur ? '#fff' : 'transparent',
                  font: 'inherit', fontSize: 14, fontWeight: cur ? 700 : 500, color: C.text, textAlign: 'left' }}>
                <span aria-hidden="true" style={{ width: 22, height: 22, flex: '0 0 22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, background: p.complete ? C.ok : cur ? C.navy : '#e0e0d8', color: p.complete || cur ? '#fff' : '#44443e' }}>
                  {p.complete ? '✓' : i + 1}
                </span>
                <span>{s.title}</span>
              </button>
            );
          })}
          <button onClick={() => go(sections.length)} aria-current={onReview ? 'step' : undefined}
            style={{ minHeight: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '0 12px', borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${onReview ? C.navy : 'transparent'}`, background: onReview ? '#fff' : 'transparent',
              font: 'inherit', fontSize: 14, fontWeight: onReview ? 700 : 500, color: C.text, textAlign: 'left' }}>
            <span aria-hidden="true" style={{ width: 22, height: 22, flex: '0 0 22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, background: onReview ? C.navy : '#e0e0d8', color: onReview ? '#fff' : '#44443e' }}>{sections.length + 1}</span>
            <span>Agreement and submit</span>
          </button>
          <div style={{ fontSize: 13, color: C.sub, padding: '8px 12px' }}>{doneCount} of {sections.length} sections complete</div>
        </nav>

        <div style={{ flex: '999 1 560px', minWidth: 0, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {!onReview && section && (
            <>
              <h2 style={{ margin: 0, fontSize: 22 }}>{section.title}</h2>
              {section.intro && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: -6, fontSize: 14.5, lineHeight: 1.55, color: '#33332e', maxWidth: 760 }}>
                  {[].concat(section.intro).map((para, n) => <p key={n} style={{ margin: 0 }}>{para}</p>)}
                </div>
              )}
              {section.tip && (
                <div style={{ background: C.warnBg, border: `1px solid ${C.warnBorder}`, borderRadius: 10, padding: '12px 14px', fontSize: 14, lineHeight: 1.5 }}>
                  <strong>Get this right: </strong>{section.tip}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18, alignItems: 'start' }}>
                {section.fields.filter(f => shown(f, answers)).map(f => (
                  <Field key={f.key} f={f} value={answers[f.key]} sectionOptional={!!section.optionalSection}
                    problem={problems[f.key] && (answers[f.key] || required(f, answers)) ? problems[f.key] : null}
                    onChange={(v) => setValue(f.key, v)} onBlur={() => save()} />
                ))}
              </div>
            </>
          )}

          {onReview && (
            <>
              <h2 style={{ margin: 0, fontSize: 22 }}>Agreement and submit</h2>
              {sections.some(s => !(progress.find(p => p.key === s.key) || {}).complete) && (
                <div style={{ background: C.warnBg, border: `1px solid ${C.warnBorder}`, borderRadius: 10, padding: '12px 14px', lineHeight: 1.5 }}>
                  Still to finish:{' '}
                  {sections.map((s, i) => ((progress.find(p => p.key === s.key) || {}).complete ? null : (
                    <button key={s.key} onClick={() => go(i)} style={{ background: 'none', border: 'none', padding: 0, margin: '0 10px 0 0', font: 'inherit', color: C.navy, textDecoration: 'underline', cursor: 'pointer' }}>{s.title}</button>
                  )))}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontWeight: 700 }}>{view.terms.title}: the short version</div>
                <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
                  {(view.terms.summary || []).map(t => <li key={t}>{t}</li>)}
                </ul>
                <a href={view.terms.agreementUrl} target="_blank" rel="noreferrer" style={{ color: C.navy, fontWeight: 600 }}>
                  Read the full agreement (opens in a new tab)
                </a>
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minHeight: 44, fontWeight: 600, lineHeight: 1.45 }}>
                <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
                  style={{ width: 22, height: 22, marginTop: 1, accentColor: C.navy, flex: '0 0 auto' }} />
                I have read and accept the {view.terms.title} (version {view.terms.version}) on behalf of {answers['registration.legalName'] || answers.name || view.shopName}, and I am authorized to do so.
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 420 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>Type your full name</span>
                <input value={termsName} onChange={e => setTermsName(e.target.value)} style={inputStyle(false)} autoComplete="name" />
              </label>
              {submitError && <div role="alert" style={{ color: C.error }}>{submitError}</div>}
              <div>
                <button onClick={submit} disabled={busy || !termsAccepted || termsName.trim().length < 3} style={{ ...btn(true), minHeight: 48, opacity: (busy || !termsAccepted || termsName.trim().length < 3) ? 0.55 : 1 }}>
                  {busy ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingTop: 8, borderTop: '1px solid #ecece4' }}>
            <button onClick={() => go(Math.max(at - 1, 0))} disabled={at === 0} style={{ ...btn(false), opacity: at === 0 ? 0.5 : 1 }}>Back</button>
            <span aria-live="polite" style={{ fontSize: 13, color: saveState.startsWith('Not') ? C.error : C.sub }}>{saveState}</span>
            {!onReview && <button onClick={() => go(at + 1, { confirm: true })} style={btn(true)}>Save and continue</button>}
          </div>
        </div>
      </div>
    </Page>
  );
}

function Page({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: font, fontSize: 15 }}>
      <div style={{ background: C.navy, color: '#fff', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>spinboard<span style={{ color: C.accent }}>.</span></div>
        <div style={{ fontSize: 13, color: '#c9d3df' }}>Shop setup</div>
      </div>
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '32px 20px 56px', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {children}
      </div>
    </div>
  );
}
